import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import SeoHead from '@/components/feature/SeoHead';
import PageHero from '@/components/feature/PageHero';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { aboutFaqs } from '@/mocks/faqs';
import { siteInfo } from '@/mocks/site';
import { liveSessions } from '@/mocks/lives';
import { replays } from '@/mocks/replays';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '只做整理，不做渲染',
    text: '排期信息来自各直播间公开的开播预告，我们只做时间校对与栏目归类，不使用夸张标题吸引点击。',
  },
  {
    icon: 'ri-time-line',
    title: '时间信息优先准确',
    text: '每 15 分钟校对一次排期，遇到临时改档会立即更新，并在页面上标注最近一次校验的时间。',
  },
  {
    icon: 'ri-links-line',
    title: '来源可追溯',
    text: '每场直播都保留主播、栏目与原始场次信息，方便观众自行核对，也方便主播反馈修正。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '不收观众费用',
    text: '浏览排期、查看回放列表与设置提醒均免费，网站不代收任何打赏或门票费用。',
  },
];

const steps = [
  { step: '01', title: '抓取公开预告', text: '从各直播间公开的开播预告中收集场次名称、时间与主播信息。' },
  { step: '02', title: '校对与归类', text: '核对时间是否冲突，按八个栏目归类，并标记热门与专题场次。' },
  { step: '03', title: '生成时间轴', text: '按开播时间排序，区分正在直播、即将开始与已结束三种状态。' },
  { step: '04', title: '提醒与回放', text: '重点场次提前 30 分钟发送提醒，结束后 2 小时内整理回放入口。' },
];

const voices = [
  {
    name: '周一下午的鱼',
    role: '追更娱乐场次',
    text: '以前要一个个点开直播间看开没开，现在打开排期页就知道几点有什么，倒计时也准。',
  },
  {
    name: '老陈不加班',
    role: '财经栏目观众',
    text: '最实用的是回放列表，按播放量排一下就能挑出重点场次，通勤路上正好听完一场复盘。',
  },
  {
    name: '半糖不加冰',
    role: '深夜场常客',
    text: '设提醒这个功能挺省心，重点场次会提前发邮件，不用一直惦记着时间。',
  },
];

export default function AboutPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://3yp0uh.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://3yp0uh.cn/about' },
        ],
      },
      {
        '@type': 'AboutPage',
        name: '关于爆料直播网',
        url: 'https://3yp0uh.cn/about',
        inLanguage: 'zh-CN',
        description: '爆料直播网的品牌介绍、网站定位、内容原则与联系方式。',
      },
      {
        '@type': 'Organization',
        name: '爆料直播网',
        url: 'https://3yp0uh.cn/',
        email: siteInfo.email,
        description: '直播排期整理与呈现平台，覆盖八个内容方向，提供排期、回放与提醒服务。',
      },
      {
        '@type': 'FAQPage',
        mainEntity: aboutFaqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };

  return (
    <SiteLayout>
      <SeoHead
        title="关于我们 - 爆料直播网内容原则与联系方式"
        description="了解爆料直播网的品牌定位、内容原则与排期整理流程，以及回放保留规则、提醒服务说明和联系方式。"
        keywords="关于爆料直播网,爆料直播,内容原则,直播排期整理,联系方式"
        path="/about"
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="关于爆料直播网"
        title="让每一场直播都被按时看到"
        desc="我们是一家专注直播排期整理的内容平台，把分散在各处的开播信息汇聚成一张清晰的时间表，让观众少花时间找，多花时间看。"
        crumbs={[{ label: '首页', path: '/' }, { label: '关于我们' }]}
      />

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <span className="block aspect-[4/3] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20workspace%20with%20a%20large%20schedule%20board%20and%20sticky%20notes%20in%20amber%20and%20brown%20tones%2C%20soft%20natural%20daylight%2C%20calm%20minimal%20photography%2C%20high%20detail&width=1000&height=750&seq=bl-about-01&orientation=landscape"
                alt="爆料直播网 - 直播排期整理工作台"
                title="爆料直播网 直播排期整理 关于我们"
                className="h-full w-full object-cover object-top"
              />
            </span>
          </div>
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-800">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-book-open-line" aria-hidden="true" />
              </span>
              品牌介绍
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
              一张时间表，装下全天的直播安排
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              {siteInfo.name}（{siteInfo.shortName}）把当天各档直播的时间、主播、栏目和看点集中整理在同一页面。
              观众不必再逐个直播间确认开播状态，打开首页就能看到今日直播排期、即将开始场次和正在进行中的内容。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              我们希望排期这件事足够朴素：时间准确、归类清楚、状态明确。至于看哪一场、看多久，
              交给观众自己决定，网站只负责把选项摆整齐。
            </p>
            <dl className="mt-7 grid grid-cols-3 gap-4 border-t border-background-200 pt-6">
              <div>
                <dt className="text-xs text-foreground-500">每日场次</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">
                  {liveSessions.length} 场
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">回放收录</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">
                  {replays.length} 条
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">内容方向</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">8 个</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-12 md:py-16">
        <div className="container-page">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-compass-3-line" aria-hidden="true" />
            </span>
            内容原则
          </span>
          <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[28px]">
            我们坚持的四条底线
          </h2>
          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                  <i className={item.icon} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-800">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-flow-chart" aria-hidden="true" />
              </span>
              运作流程
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[28px]">
              一场直播从预告到回放，会经过这四步
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              流程尽量保持透明：所有信息都取自公开渠道，整理结果会标注更新时间，
              如果发现时间或归类有误，可以通过页脚邮箱反馈，我们会优先核对并修正。
            </p>
            <Link
              to="/schedule"
              className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              去看今天的排期
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </span>
            </Link>
          </div>

          <ol className="flex flex-col gap-4">
            {steps.map((item) => (
              <li key={item.step} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="font-heading text-2xl font-bold text-background-300">{item.step}</span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-full bg-background-100 py-12 md:py-16">
        <div className="container-page">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-chat-smile-2-line" aria-hidden="true" />
            </span>
            用户口碑
          </span>
          <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[28px]">
            观众最常提到的三件事
          </h2>
          <div className="mt-7 grid grid-cols-1 gap-5 md:grid-cols-3">
            {voices.map((item) => (
              <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-8 h-8 flex items-center justify-center text-2xl text-primary-300">
                  <i className="ri-double-quotes-l" aria-hidden="true" />
                </span>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.text}
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-12">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-question-line" aria-hidden="true" />
              </span>
              常见问题
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[28px]">
              关于内容来源与回放的疑问
            </h2>
            <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">联系方式</h3>
              <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                    <i className="ri-mail-line" aria-hidden="true" />
                  </span>
                  <a href={`mailto:${siteInfo.email}`} className="transition-colors hover:text-primary-700">
                    {siteInfo.email}
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                    <i className="ri-time-line" aria-hidden="true" />
                  </span>
                  {siteInfo.workTime}
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 w-4 h-4 flex items-center justify-center text-primary-600">
                    <i className="ri-feedback-line" aria-hidden="true" />
                  </span>
                  排期有误、栏目建议或合作沟通，都可以通过上面的邮箱联系我们。
                </li>
              </ul>
            </div>
          </div>

          <FaqAccordion items={aboutFaqs} />
        </div>
      </section>
    </SiteLayout>
  );
}